// import { MedicationApi } from '../../generated/api' // TODO: Re-enable when API generation is fixed
import type { Api } from '../types/generated'
import type { 
  Medication,
  MedicationsQueryParams 
} from '../types/api'
import { getApiConfiguration } from '../config/apiConfig'

export interface MedicationsResponse {
  items: Medication[]
  total_items: number
  page_size: number
  page_number: number
  has_more: boolean
  page_next?: string | null
  page_previous?: string | null
}

export const medicationsApi = {
  // GET /medication - list all medications with search support
  async getMedications(params?: MedicationsQueryParams): Promise<Medication[]> {
    const config = getApiConfiguration()
    
    // WORKAROUND: The generated API doesn't properly support query parameters
    // So we'll make a direct fetch call with proper query string construction
    // TODO: Switch back to generated client when API generation is fixed:
    // const api = new MedicationApi(config)
    // return api.get1(params?.q, params?.page, params?.limit)
    
    let url = `${config.basePath}/medication`
    const queryParams = new URLSearchParams()
    
    if (params?.q) {
      // Handle search query - if it contains spaces, treat as multiple search terms
      const searchTerms = params.q.includes(' ') 
        ? params.q.split(' ').filter(term => term.trim())
        : [params.q]
      
      // Append each search term as a separate name parameter for array handling
      searchTerms.forEach(term => queryParams.append('name', term))
    }
    if (params?.limit) {
      queryParams.append('page_size', params.limit.toString())
    }
    if (params?.page_number) {
      queryParams.append('page_number', params.page_number.toString())
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString()
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch medications: ${response.statusText}`)
    }
    
    const data = await response.json()
    
    // Convert the response to our expected format
    let medications: Medication[] = []
    
    if (Array.isArray(data)) {
      medications = data.map((item: any) => ({
        id: item.id || '',
        name: item.name || 'Unknown',
        category: item.category || ''
      }))
    } else if (data && typeof data === 'object' && 'items' in data) {
      // If response has items property (paginated)
      const items = data.items || []
      medications = items.map((item: any) => ({
        id: item.id || '',
        name: item.name || 'Unknown', 
        category: item.category || ''
      }))
    }
    
    return medications
  },

  // GET /medication - with full pagination support for infinite scroll
  async getMedicationsPaginated(params?: {
    q?: string;
    page_size?: number;
    page_number?: number;
  }): Promise<MedicationsResponse> {
    const config = getApiConfiguration()
    
    let url = `${config.basePath}/medication`
    const queryParams = new URLSearchParams()
    
    if (params?.q) {
      // Handle search query - if it contains spaces, treat as multiple search terms
      const searchTerms = params.q.includes(' ') 
        ? params.q.split(' ').filter(term => term.trim())
        : [params.q]
      
      // Append each search term as a separate name parameter for array handling
      searchTerms.forEach(term => queryParams.append('name', term))
    }
    if (params?.page_size) {
      queryParams.append('page_size', params.page_size.toString())
    }
    if (params?.page_number) {
      queryParams.append('page_number', params.page_number.toString())
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString()
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch medications: ${response.statusText}`)
    }
    
    const data = await response.json()
    
    // Parse paginated response
    const items = Array.isArray(data) ? data : (data?.items || [])
    const medications: Medication[] = items.map((item: any) => ({
      id: item.id || '',
      name: item.name || 'Unknown',
      category: item.category || ''
    }))
    
    // Extract pagination info
    const total_items = data?.total_items || medications.length
    const page_size = data?.page_size || params?.page_size || 20
    const page_number = data?.page_number || params?.page_number || 1
    const has_more = data?.page_next ? true : (page_number * page_size < total_items)
    
    return {
      items: medications,
      total_items,
      page_size,
      page_number,
      has_more,
      page_next: data?.page_next || null,
      page_previous: data?.page_previous || null
    }
  },
}